import { useEffect, useMemo, useState } from 'react';
import type { League, SportEvent } from '../api';
import { dayLabel, SportRow } from '../components/LiveBits';
import { goBack } from '../router';
import { loadLeagues, loadWeek } from '../sport';

type Tab = 'live' | 'today' | 'week';

/** Competitiepagina: wat er nu live is, vandaag en deze week, met alle zenders. */
export function SportLeague({ id }: { id: string }) {
  const [league, setLeague] = useState<League | null>(null);
  const [events, setEvents] = useState<SportEvent[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab | null>(null);
  const [broken, setBroken] = useState(false);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    let live = true;
    const load = (force: boolean) =>
      Promise.all([loadLeagues(), loadWeek(force)])
        .then(([ls, evs]) => {
          if (!live) return;
          setLeague(ls.find((l) => l.id === id) ?? null);
          setEvents(evs.filter((e) => e.league === id));
          setNow(Date.now());
        })
        .catch(() => live && setError('Sportgids laden mislukt.'));
    void load(false);
    const t = window.setInterval(() => void load(true), 60_000);
    return () => {
      live = false;
      window.clearInterval(t);
    };
  }, [id]);

  const groups = useMemo(() => {
    const list = (events ?? []).filter((e) => e.stop > now).sort((a, b) => a.start - b.start);
    const today = new Date(now).toDateString();
    return {
      live: list.filter((e) => e.live),
      today: list.filter((e) => !e.live && new Date(e.start).toDateString() === today),
      week: list.filter((e) => !e.live),
    };
  }, [events, now]);

  // Begin op het tabblad waar iets staat
  const active: Tab = tab ?? (groups.live.length ? 'live' : groups.today.length ? 'today' : 'week');
  const shown = groups[active];

  // Week: per dag groeperen
  const byDay = useMemo(() => {
    const m = new Map<string, SportEvent[]>();
    for (const e of shown) {
      const k = dayLabel(e.start, now);
      m.set(k, [...(m.get(k) ?? []), e]);
    }
    return [...m.entries()];
  }, [shown, now]);

  const img = league?.badge ?? league?.logo;
  const channels = new Set((events ?? []).flatMap((e) => e.channels.map((c) => c.name))).size;

  return (
    <main className="page league-page">
      <header className="league-head">
        <button className="btn" onClick={goBack} aria-label="Terug">‹ Terug</button>
        <span className="league-logo big" style={{ background: league?.color ?? '#1C212B' }}>
          {img && !broken ? <img src={img} alt="" referrerPolicy="no-referrer" onError={() => setBroken(true)} /> : <span className="league-abbr">{league?.abbr ?? ''}</span>}
        </span>
        <div>
          <h1>{league?.name ?? 'Sport'}</h1>
          <p className="muted">
            {events === null ? 'Laden…' : `${groups.live.length} live · ${groups.today.length} vandaag · ${groups.week.length} deze week${channels ? ` · op ${channels} zenders` : ''}`}
          </p>
        </div>
      </header>

      <div className="chip-wrap" role="tablist" aria-label="Wanneer" data-focus-group>
        {([['live', `Nu live (${groups.live.length})`], ['today', `Vandaag (${groups.today.length})`], ['week', `Komende 7 dagen (${groups.week.length})`]] as const).map(([t, label]) => (
          <button key={t} role="tab" className="chip" aria-selected={active === t} aria-pressed={active === t} onClick={() => setTab(t)}>{label}</button>
        ))}
      </div>

      {error && <p className="error">{error}</p>}
      {events && !shown.length && (
        <p className="muted">{active === 'live' ? 'Er is nu niets live.' : active === 'today' ? 'Vandaag niets meer.' : 'Deze week niets gevonden in de gids.'}</p>
      )}
      {active === 'week' ? (
        byDay.map(([day, list]) => (
          <section key={day} className="sport-section" aria-label={day}>
            <h2>{day}</h2>
            {list.map((e) => <SportRow key={e.id} e={e} />)}
          </section>
        ))
      ) : (
        <section className="sport-section">{shown.map((e) => <SportRow key={e.id} e={e} />)}</section>
      )}
    </main>
  );
}
